import { useState, useEffect } from 'react'
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'

const API_BASE_URL = '/api/v1/dev'

interface Allergen { id: number; name: string }
interface Brand {
  id: number
  name: string
  ingredients_text: string | null
  allergens: Allergen[]
}

interface Props {
  ingredientId: number
  allergens: Allergen[]
  onError?: (msg: string) => void
}

// Gestiona las marcas de un ingrediente (CRUD inline) dentro de su ficha.
export default function BrandsManager({ ingredientId, allergens, onError }: Props) {
  const [brands, setBrands] = useState<Brand[]>([])
  const [editing, setEditing] = useState<Brand | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [ingredientsText, setIngredientsText] = useState('')
  const [allergenIds, setAllergenIds] = useState<number[]>([])
  const [saving, setSaving] = useState(false)

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/ingredients/${ingredientId}/brands`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) setBrands(data.data || [])
    } catch {
      onError?.('No se pudieron cargar las marcas')
    }
  }

  useEffect(() => { fetchBrands() }, [ingredientId])

  const resetForm = () => {
    setEditing(null); setName(''); setIngredientsText(''); setAllergenIds([]); setShowForm(false)
  }

  const openNew = () => { resetForm(); setShowForm(true) }
  const openEdit = (b: Brand) => {
    setEditing(b); setName(b.name); setIngredientsText(b.ingredients_text || '')
    setAllergenIds(b.allergens.map(a => a.id)); setShowForm(true)
  }

  const toggleAllergen = (id: number) =>
    setAllergenIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const save = async () => {
    if (!name.trim()) { onError?.('La marca necesita un nombre'); return }
    setSaving(true)
    try {
      const url = editing
        ? `${API_BASE_URL}/ingredients/${ingredientId}/brands/${editing.id}`
        : `${API_BASE_URL}/ingredients/${ingredientId}/brands`
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name, ingredients_text: ingredientsText || null, allergen_ids: allergenIds }),
      })
      const data = await res.json()
      if (res.ok && data.success) { resetForm(); fetchBrands() }
      else onError?.(data.message || 'No se pudo guardar la marca')
    } catch {
      onError?.('Error de conexión')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (b: Brand) => {
    if (!confirm(`¿Eliminar la marca "${b.name}"?`)) return
    try {
      const res = await fetch(`${API_BASE_URL}/ingredients/${ingredientId}/brands/${b.id}`, { method: 'DELETE', headers: { Accept: 'application/json' } })
      const data = await res.json()
      if (res.ok && data.success) fetchBrands()
      else onError?.(data.message || 'No se pudo eliminar')
    } catch {
      onError?.('Error de conexión')
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">Marcas</label>
        {!showForm && (
          <button type="button" onClick={openNew} className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
            <PlusIcon className="h-4 w-4 mr-1" /> Añadir marca
          </button>
        )}
      </div>

      {brands.length > 0 ? (
        <div className="space-y-2 mb-3">
          {brands.map(b => (
            <div key={b.id} className="border border-gray-200 rounded p-2 flex items-start justify-between">
              <div className="text-sm">
                <div className="font-medium text-gray-900">{b.name}</div>
                {b.ingredients_text && <div className="text-gray-600">{b.ingredients_text}</div>}
                {b.allergens.length > 0 && (
                  <div className="text-xs text-gray-500 mt-0.5">Alérgenos: {b.allergens.map(a => a.name).join(', ')}</div>
                )}
              </div>
              <div className="flex gap-2 ml-2">
                <button type="button" onClick={() => openEdit(b)} className="text-yellow-600 hover:text-yellow-800" title="Editar"><PencilIcon className="h-4 w-4" /></button>
                <button type="button" onClick={() => remove(b)} className="text-red-600 hover:text-red-800" title="Eliminar"><TrashIcon className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        !showForm && <p className="text-sm text-gray-400 mb-3">Sin marcas. Si añades marcas, será obligatorio elegir una al recibir este ingrediente.</p>
      )}

      {showForm && (
        <div className="border border-blue-200 bg-blue-50 rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">{editing ? 'Editar marca' : 'Nueva marca'}</span>
            <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Nombre de la marca (p. ej. Filadelfia)"
            className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
          />
          <textarea
            value={ingredientsText} onChange={e => setIngredientsText(e.target.value)}
            placeholder="Ingredientes según su etiqueta (p. ej. leche, fermentos, nata, sorbato)"
            rows={2}
            className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
          />
          <div>
            <span className="block text-xs font-medium text-gray-700 mb-1">Alérgenos</span>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-28 overflow-y-auto border border-gray-200 bg-white rounded p-2">
              {allergens.map(a => (
                <label key={a.id} className="flex items-center">
                  <input type="checkbox" checked={allergenIds.includes(a.id)} onChange={() => toggleAllergen(a.id)} className="mr-2" />
                  <span className="text-sm">{a.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={resetForm} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancelar</button>
            <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Guardando…' : (editing ? 'Actualizar' : 'Añadir')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
